import { OnboardingDecisionChoice } from "@gamedev-web/one-gaming-crm";

import {
  AaaGameConceptDataSet,
  FieldOption,
  GameConceptsQuery,
  IdXboxGameConceptDataSet,
  PitchConceptDataSet,
} from "../server/crm/api/models/forms";
import { IdXboxGameConceptData } from "./forms";
import { NdaStatus } from "./status";

/* CrmIdXboxApplicationStatus and CrmGameConceptStatus are exclusive data coming from CRM. They are indicators of how the process is going from the server-side
that is needed to later calculate the current step of the application.
For example: we may calculate client-side that the user is ready to submit an application, but such application had already
been submitted before. By getting the 'NdaUnderReview' flag, we know we are in ApplicationStep.ApplicationIsBeingReviewed */
export enum CrmApplicationStatus {
  Unset = "Unset",
  SoftApprovalDenied = "SoftApprovalDenied",
  Rejected = "Rejected",
  NdaUnderReview = "NdaUnderReview",
  NdaSent = "NdaSent",
  NdaSigned = "NdaSigned",
}

export const CrmApplicationStatusOrder = [
  CrmApplicationStatus.Unset,
  CrmApplicationStatus.SoftApprovalDenied,
  CrmApplicationStatus.Rejected,
  CrmApplicationStatus.NdaUnderReview,
  CrmApplicationStatus.NdaSent,
  CrmApplicationStatus.NdaSigned,
];

export enum CrmGameConceptStatus {
  Unset = "Unset",
  UnderReview = "UnderReview",
  OnHold = "OnHold",
  MoreInfoNeeded = "MoreInfoNeeded",
  AdditionalContractsSent = "AdditionalContractsSent",
  Accepted = "Accepted", // This means the concept got approved AND the TLA got signed
  Rejected = "Rejected",
}

export const CrmGameConceptStatusOrder2 = {
  [CrmGameConceptStatus.Rejected]: 1,
  [CrmGameConceptStatus.Unset]: 2,
  [CrmGameConceptStatus.OnHold]: 3,
  [CrmGameConceptStatus.MoreInfoNeeded]: 4,
  [CrmGameConceptStatus.UnderReview]: 5,
  [CrmGameConceptStatus.AdditionalContractsSent]: 6,
  [CrmGameConceptStatus.Accepted]: 7,
};

export enum CrmGameConceptSubStatus {
  Unset = "Unset",
  RejectedSoftAlreadyHaveDevToken = "RejectedSoftAlreadyHaveDevToken",
  RejectedSoftGeneral = "RejectedSoftGeneral",
  RejectedHard = "RejectedHard",
  NeedMoreInformation = "NeedMoreInformation",
  NeedBuildLink = "NeedBuildLink",
  NeedVideoLink = "NeedVideoLink",
  NeedBuildAndVideoLink = "NeedBuildAndVideoLink",
  ReviewSimShip = "ReviewSimShip",
  ReviewTooEarly = "ReviewTooEarly",
}

export const CrmGameConceptStatusOrder = [
  CrmGameConceptStatus.Unset,
  CrmGameConceptStatus.OnHold,
  CrmGameConceptStatus.MoreInfoNeeded,
  CrmGameConceptStatus.UnderReview,
  CrmGameConceptStatus.AdditionalContractsSent,
  CrmGameConceptStatus.Accepted,
];

export type CurrentGameConcept = {
  id: string | undefined;
  crmGameConceptStatus: CrmGameConceptStatus | undefined;
  crmGameConceptSubStatus: CrmGameConceptSubStatus | undefined;
  crmGameConceptApprovalComments: string | undefined;
  submittedDate: Date | undefined;
  acceptedDate: Date | undefined;
  forms?: IdXboxGameConceptData;
};

export enum BlockedReasons {
  none = "NONE",
  bannedInPartnerCenter = "BANNED",
  unsupportedPartnerCenterAccountType = "UNSUPPORTED_PARTNER_CENTER_ACCOUNT_TYPE",
  legacyPartnerInPartnerCenter = "PARTNER",
  legacyAccount = "LEGACY",
  hubAccessRequired = "HUB_ACCESS_REQUIRED",
}

//* ***************** Crm Status ******************/
export type CrmStatusDataBase = {
  crmApplicationStatus: CrmApplicationStatus;
  blockedReason: BlockedReasons;
  ndaSignedDate: Date | undefined;
  ndaStatus: NdaStatus | undefined;
  onboardingDecision: OnboardingDecisionChoice | undefined;
};

export type SelectionsData = {
  countriesOrRegions: Array<FieldOption>;
  genres: Array<FieldOption>;
  middlewareEngines: Array<FieldOption>;
  ratings: Array<FieldOption>;
};

export type AaaSelectionsData = {
  countriesOrRegions: Array<FieldOption>;
  genres: Array<FieldOption>;
  middlewareEngines: Array<FieldOption>;
  worldwideRatings: {
    esrb: Array<FieldOption>;
    germanyUsk: Array<FieldOption>;
    taiwanCsrr: Array<FieldOption>;
    europeIsfe: Array<FieldOption>;
    australiaAcb: Array<FieldOption>;
    russiaPcbp: Array<FieldOption>;
    japanCero: Array<FieldOption>;
    koreaGrb: Array<FieldOption>;
    saudiArabiaGamr: Array<FieldOption>;
    brazilRatings: Array<FieldOption>;
    newZealandOflc: Array<FieldOption>;
  };
  spatial3dSoundList: Array<FieldOption>;
};

export type CrmGameConceptStatusDataBase = {
  currentGameConcept: CurrentGameConcept | undefined;
  gameConceptsListQuery: GameConceptsQuery | undefined;
};

export type CrmIdXboxGameConceptStatusData = {
  gameConceptsList: IdXboxGameConceptDataSet;
  hasAtLeastOneAcceptedApplication: boolean;
  highestCrmGameConceptStatus: CrmGameConceptStatus | undefined;
} & CrmGameConceptStatusDataBase;

export type CrmAaaGameConceptStatusData = {
  gameConceptsList: AaaGameConceptDataSet;
} & CrmGameConceptStatusDataBase;

export type CrmGdkStatusData = {
  isAtLeastOneSignedGdkContract: boolean;
  isGdkReceived: boolean;
};

export type CrmFastTrackStatusData = {
  isFastTrackAccount: boolean;
};

export type CrmAaaEnablingStatusData = {
  isAaaOnboardingHubEnabled: boolean;
};

// AAA
export type CrmAaaStatusData = AaaSelectionsData &
  CrmAaaGameConceptStatusData &
  CrmAaaEnablingStatusData;

// ID@Xbox
export type CrmIdXboxStatusData = CrmStatusDataBase &
  SelectionsData &
  CrmIdXboxGameConceptStatusData &
  CrmGdkStatusData &
  CrmFastTrackStatusData;

// Pitch
export enum CrmPitchConceptStatus {
  Unset = "Unset",
  Submitted = "Submitted",
  Reviewed = "Reviewed",
}

export type CurrentPitchConcept = {
  id: string | undefined;
  crmPitchConceptStatus: CrmPitchConceptStatus | undefined;
  crmPitchConceptApprovalComments: string | undefined;
};

export type CrmPitchConceptStatusData = {
  pitchConceptLists: PitchConceptDataSet;
  currentPitchConcept: CurrentPitchConcept | undefined;
};

export type CrmPitchStatusData = CrmStatusDataBase & CrmPitchConceptStatusData & SelectionsData;
